import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import styled from "styled-components/macro";
import { Loader } from "@fluentui/react-northstar";

import useAnnualSummary from "../../hooks/useAnnualSummary";
import SliderYears from "../SliderYears";
import { SUMMARY_SCOPES } from "./constants";
import { Card, ResetButton } from "./index.style";
import { KpiBanner, buildSummaryKpis } from "./KpiBanner";
import { MonthStatusLegend } from "./MonthStatusBar";
import TodoButtons from "./TodoButtons";
import StatusFilterDropdown from "./StatusFilterDropdown";
import SearchTokens from "./SearchTokens";
import SummaryTable from "./SummaryTable";
import SelectionBar from "./SelectionBar";
import ExportSummaryDialog from "./ExportSummaryDialog";

/**
 * Synthèse annuelle des onglets Mon équipe et Entreprise
 * @param {{ scope: "manager"|"hr" }} props
 */
const AnnualSummary = ({ scope }) => {
    const { viewActionsNode } = useOutletContext() ?? {};
    const [isExportOpen, setIsExportOpen] = useState(false);

    const {
        year,
        setYear,
        summary,
        filters,
        ud,
        status,
        resetFilters,
        selection,
        exportSummary,
        isExporting,
    } = useAnnualSummary(scope);

    const isHR = scope === SUMMARY_SCOPES.hr;
    const data = summary.data;
    const isFirstLoad = summary.isLoading && !data;

    const todoButtons = (
        <TodoButtons
            todo={data?.todo}
            selectedStatus={status.value}
            onPick={status.pick}
        />
    );

    return (
        <Page>
            {viewActionsNode
                ? createPortal(todoButtons, viewActionsNode)
                : todoButtons}

            <KpiBanner
                items={buildSummaryKpis(data?.kpis)}
                loading={isFirstLoad}
            />

            <Card>
                <Toolbar>
                    <SliderYears
                        defaultYear={year}
                        onChangeYear={({ year }) => setYear(Number(year))}
                    />
                    <MonthStatusLegend />
                    <Filters>
                        <StatusFilterDropdown
                            value={status.value}
                            counts={filters.data?.status_counts}
                            onToggle={status.toggle}
                        />
                        <SearchTokens
                            groups={isHR ? filters.data?.groups : []}
                            users={filters.data?.users}
                            value={ud.value}
                            onToggle={ud.toggle}
                            onClear={ud.clear}
                            placeholder={
                                isHR
                                    ? "Rechercher un département ou un collaborateur…"
                                    : "Rechercher un collaborateur…"
                            }
                        />
                    </Filters>
                </Toolbar>

                {summary.isError ? (
                    <ErrorMessage>
                        <span>{summary.error?.message}</span>
                        {(ud.value.length > 0 || status.value.length > 0) && (
                            <ResetButton onClick={resetFilters}>
                                Réinitialiser les filtres
                            </ResetButton>
                        )}
                    </ErrorMessage>
                ) : isFirstLoad ? (
                    <Loader styles={{ padding: "40px" }} />
                ) : (
                    <SummaryTable
                        groups={data?.groups}
                        total={data?.total}
                        highlight={status.value}
                        selection={selection}
                        onResetFilters={resetFilters}
                        loading={summary.isFetching}
                    />
                )}
            </Card>

            {!isExportOpen && (
                <SelectionBar
                    count={selection.value.length}
                    onClear={selection.clear}
                    onExport={() => setIsExportOpen(true)}
                />
            )}

            <ExportSummaryDialog
                open={isExportOpen}
                year={year}
                isExporting={isExporting}
                onClose={() => setIsExportOpen(false)}
                onExport={(period) =>
                    exportSummary({
                        ...period,
                        onSuccess: () => setIsExportOpen(false),
                    })
                }
            />
        </Page>
    );
};

export default AnnualSummary;

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 0 120px;
    width: 100%;
`;

const Toolbar = styled.div`
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px 24px;
    flex-wrap: wrap;
    border-bottom: 1px solid #edebe9;
`;

const Filters = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 14px;
    flex-wrap: wrap;
    flex: 1 1 400px;
`;

const ErrorMessage = styled.div`
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    color: #c4314b;
    font-size: 14px;
`;
